import { FC, useEffect, useRef, useState } from 'react';
import cx from 'classnames';

import { ReactComponent as BlueArrow } from 'assets/image/selects/blue-arrow.svg';
import { ReactComponent as WhiteArrow } from 'assets/image/selects/white-arrow.svg';
import {
	SEARCH_ORGANIZATION_BY_ADMIN_POSSIBLE_SEARCH,
	SearchOrganisationByAdminResponse,
	SearchOrganizationByAdminRequest,
} from '@joc/api-gateway';
import { API } from 'core/API';
import { useDebounceValue } from 'core/customHooks';
import Loader from 'shared/components/Loader';

import dropdownStyles from 'shared/components/Selects/SelectDropdown/index.module.scss';
import styles from './CustomPushNotifications.module.scss';

type OrganizationDropdownProps = {
	value: number | null;
	onChange: (organizationId: number) => void;
	onBlur: () => void;
};

const OrganizationDropdown: FC<OrganizationDropdownProps> = ({ value, onChange, onBlur }) => {
	const [options, setOptions] = useState<SearchOrganisationByAdminResponse[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [hasError, setHasError] = useState(false);
	const [isOpen, setIsOpen] = useState(false);
	const [query, setQuery] = useState('');
	const [selectedLabel, setSelectedLabel] = useState('');
	const [reloadToken, setReloadToken] = useState(0);
	const selectRef = useRef<HTMLDivElement>(null);
	const searchRef = useRef<HTMLInputElement>(null);
	const debouncedQuery = useDebounceValue(query, 300);

	useEffect(() => {
		if (!value) setSelectedLabel('');
	}, [value]);

	useEffect(() => {
		if (debouncedQuery === undefined) return undefined;

		let cancelled = false;
		const search = debouncedQuery.trim();

		const loadOrganizations = async () => {
			try {
				setHasError(false);
				setIsLoading(true);
				const response = await API.searchOrganizationsByAdmin(
					SearchOrganizationByAdminRequest.fromJS({
						...(search
							? {
									fullTextSearch: {
										value: search,
										fields: [SEARCH_ORGANIZATION_BY_ADMIN_POSSIBLE_SEARCH.Name],
									},
							  }
							: {}),
					})
				);
				if (!cancelled) setOptions(response.records || []);
			} catch {
				if (!cancelled) setHasError(true);
			} finally {
				if (!cancelled) setIsLoading(false);
			}
		};

		loadOrganizations().finally();

		return () => {
			cancelled = true;
		};
	}, [debouncedQuery, reloadToken]);

	useEffect(() => {
		const handleClickOutside = (event: Event) => {
			if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
				if (isOpen) onBlur();
				setIsOpen(false);
			}
		};

		document.addEventListener('click', handleClickOutside, true);
		return () => document.removeEventListener('click', handleClickOutside, true);
	}, [isOpen, onBlur]);

	useEffect(() => {
		if (isOpen) searchRef.current?.focus();
	}, [isOpen]);

	const matchedName = options.find(option => Number(option.id) === Number(value))?.organizationName;
	const triggerLabel = matchedName || selectedLabel;

	const toggleOpen = () => {
		if (isOpen) onBlur();
		setIsOpen(open => !open);
	};

	return (
		<div
			className={cx(dropdownStyles.dropdown_select, styles.menu, { [styles.menu_open]: isOpen })}
			ref={selectRef}
		>
			<div
				className={cx(dropdownStyles.dropdown_select__title, styles.trigger, {
					[dropdownStyles.dropdown_select__title_selected]: !!value,
				})}
				onClick={toggleOpen}
			>
				<span>{triggerLabel || 'Select organization'}</span>
				{isOpen ? (
					<WhiteArrow className={cx(dropdownStyles.dropdown_select__title__vector, styles.arrowOpen)} />
				) : (
					<BlueArrow className={dropdownStyles.dropdown_select__title__vector} />
				)}
			</div>
			{isOpen && (
				<div className={styles.panel}>
					<input
						ref={searchRef}
						className={styles.search}
						value={query}
						placeholder="Search organization"
						onChange={event => setQuery(event.target.value)}
						onKeyDown={event => {
							if (event.key === 'Enter') event.preventDefault();
						}}
						onClick={event => event.stopPropagation()}
					/>
					<div className={styles.list}>
						{isLoading ? (
							<Loader />
						) : hasError ? (
							<button
								type="button"
								className={styles.retry}
								onClick={() => setReloadToken(token => token + 1)}
							>
								Something went wrong, please click to try again
							</button>
						) : options.length ? (
							options.map(option => (
								<div
									key={option.id}
									className={styles.option}
									onClick={() => {
										setSelectedLabel(option.organizationName || '');
										onChange(Number(option.id));
										setIsOpen(false);
										setQuery('');
									}}
								>
									{option.organizationName}
								</div>
							))
						) : (
							<div className={styles.empty}>No organizations</div>
						)}
					</div>
				</div>
			)}
		</div>
	);
};

export default OrganizationDropdown;
